import { WithdrawalProvider } from '../CustomOverlay/RewardSelection/withdrawal-provider.enum';
import { lazy, Suspense, useState } from 'react';

import { WithdrawalOptionType } from '../CustomOverlay/RewardSelection/withdrawal-option-type.enum';
import { MyRewardsTabType } from '../MyRewards/MyRewardsTabs/my-rewards-tab-type.enum';
import { ToastType } from '../Toast/toast-type.enum';
import { useNavigate } from 'react-router-dom';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import { ActivityLogHistoryDto } from 'shared/interfaces/user/my-rewards/activity-log-history.dto';
import { RewardHistoryDto } from 'shared/interfaces/user/my-rewards/reward-history.dto';
import { GetUserWithdrawalOptionResponseDto } from 'shared/interfaces/withdrawal/get-user-withdrawal-option-response.dto';
import { userSetting } from 'src/api/user-setting/user-setting';
import { withdrawal } from 'src/api/withdrawal/withdrawal';
import { isPayPalWithdrawalEnabled } from 'src/config/featureFlags';
import { useRewardWithdrawalContext } from 'src/contexts/RewardWithdrawalContext/RewardWithdrawalContext';
import { useUserAccountDetailsContext } from 'src/contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { useUserGamificationDetailsContext } from 'src/contexts/UserGamificationDetailsContext/UserGamificationDetailsContext';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { useVerisoul } from 'src/hooks/use-verisoul';
import { pushToDataLayer } from 'src/utils/gtm';
import { GtmEventName } from 'src/utils/gtm-event-name.enum';
import { convertLocalCurrencyToPoints } from 'src/utils/local-currency';
import { showToast } from 'src/utils/toast-utils';

const AdjustRewardAmount = lazy(() =>
  import('../CustomOverlay/AdjustRewardAmount/AdjustRewardAmount').then((module) => ({
    default: module.AdjustRewardAmount,
  }))
);
const PayPalEmailInput = lazy(() => import('../CustomOverlay/PayPalEmailInput/PayPalEmailInput'));
const ReadyToRedeem = lazy(() => import('../CustomOverlay/ReadyToRedeem/ReadyToRedeem'));
const RewardSelection = lazy(() => import('../CustomOverlay/RewardSelection/RewardSelection'));
const SuccessfulWithdrawal = lazy(() =>
  import('../CustomOverlay/SuccessfulWithdrawal/SuccessfulWithdrawal').then((module) => ({
    default: module.SuccessfulWithdrawal,
  }))
);

export const RewardWithdrawalWrapper = () => {
  const {
    isAdjustRewardAmountOpen,
    setIsAdjustRewardAmountOpen,
    isReadyToRedeemOpen,
    setIsReadyToRedeemOpen,
    rewardSelectionOpen,
    setRewardSelectionOpen,
    isSuccessfulWithdrawalOpen,
    setIsSuccessfulWithdrawalOpen,
    setActivityLog,
    setRewardHistory,
    setSelectedTab,
    setSelectedRewardDetailsWithdrawalId,
    resolveRewardTypeName,
    // PayPal withdrawal support
    paypalEmail,
    setPaypalEmail,
    isPayPalEmailInputOpen,
    setIsPayPalEmailInputOpen,
  } = useRewardWithdrawalContext();
  const { reward } = useUserGamificationDetailsContext();

  const { userSettings, refreshUserSettings } = useUserSettingsContext();
  const { accountDetails, refreshAccountDetails } = useUserAccountDetailsContext();
  const t = useTranslationByKey('COMPONENTS.REWARD_WITHDRAWAL_WRAPPER');
  const { formatCurrency } = useUserCurrency();
  const navigate = useNavigate();

  const { getSessionId } = useVerisoul();
  const [rewardSelectionSelectedRewardId, setRewardSelectionSelectedRewardId] = useState<number | undefined>(undefined);
  const [rewardSelectionMaxAvailablePoints, setRewardSelectionMaxAvailablePoints] = useState<number | undefined>(
    undefined
  );
  const [isRewardAmountAdjusting, setIsRewardAmountAdjusting] = useState(false);
  const [withdrawalId, setWithdrawalId] = useState<number | null>(null);
  const [isTrustpilotInviteEligible, setIsTrustpilotInviteEligible] = useState(false);
  const [isLastWithdrawalProcessing, setIsLastWithdrawalProcessing] = useState(false);
  const [redeemRewardSnapshot, setRedeemRewardSnapshot] = useState<GetUserWithdrawalOptionResponseDto | null>(null);
  const redeemReward = redeemRewardSnapshot ?? reward;
  const rewardPointsCost =
    redeemReward && accountDetails
      ? convertLocalCurrencyToPoints(
          redeemReward.amount,
          redeemReward.usdExchangeRate ?? accountDetails.usdExchangeRate
        )
      : 0;
  const remainingBalance = accountDetails && redeemReward ? accountDetails.currentPointsBalance - rewardPointsCost : 0;
  const canRedeem = remainingBalance >= 0;
  const currentProvider = redeemReward?.provider;

  const handleHistoryUpdate = (activityLog: ActivityLogHistoryDto, rewardHistory: RewardHistoryDto) => {
    setActivityLog(activityLog);
    setRewardHistory(rewardHistory);
  };

  const handleRedeem = async () => {
    if (!canRedeem) {
      return;
    }

    // Check if this is a PayPal withdrawal and we need to get the email
    if (isPayPalWithdrawalEnabled && currentProvider === WithdrawalProvider.PayPal && !paypalEmail) {
      setIsPayPalEmailInputOpen(true);
      return;
    }

    try {
      const verisoulSessionId = await getSessionId();
      // Mark Trustpilot invite to show after this flow completes
      localStorage.setItem('showTrustpilotModal', 'true');
      const response = await withdrawal.redeem(
        isPayPalWithdrawalEnabled && currentProvider === WithdrawalProvider.PayPal ? paypalEmail : undefined,
        verisoulSessionId ?? undefined
      );
      setWithdrawalId(response.withdrawalId);
      setIsLastWithdrawalProcessing(response.isPendingApproval);
      handleHistoryUpdate(response.activityLog, response.rewardHistory);
      if (response.isFirstWithdrawal) {
        pushToDataLayer(GtmEventName.FirstWithdrawal);
      }
      await refreshAccountDetails();
      setIsSuccessfulWithdrawalOpen(true);
      setIsReadyToRedeemOpen(false);
      setRedeemRewardSnapshot(null);
      setPaypalEmail(undefined); // Clear PayPal email after successful redemption
      await refreshUserSettings();
    } catch (error) {
      showToast(t('REDEEM_ERROR'), ToastType.Error);
    }
  };

  const handlePayPalEmailConfirm = async (email: string) => {
    if (!isPayPalWithdrawalEnabled) {
      setIsPayPalEmailInputOpen(false);
      return;
    }

    setPaypalEmail(email);
    setIsPayPalEmailInputOpen(false);

    // Save PayPal email to user settings for future use
    try {
      await userSetting.createOrUpdateUserSetting({
        settingType: UserSettingType.PayPalEmail,
        value: email,
      });
    } catch (error) {
      console.error('Failed to save PayPal email to settings:', error);
    }

    // Now proceed with the redemption (PayPal flow: paypalEmail only)
    try {
      const verisoulSessionId = await getSessionId();
      const response = await withdrawal.redeem(email, verisoulSessionId ?? undefined);
      setWithdrawalId(response.withdrawalId);
      setIsLastWithdrawalProcessing(response.isPendingApproval);
      handleHistoryUpdate(response.activityLog, response.rewardHistory);
      if (response.isFirstWithdrawal) {
        pushToDataLayer(GtmEventName.FirstWithdrawal);
      }
      await refreshAccountDetails();
      setIsSuccessfulWithdrawalOpen(true);
      setIsReadyToRedeemOpen(false);
      setRedeemRewardSnapshot(null);
      setPaypalEmail(undefined);
      await refreshUserSettings();
    } catch (error) {
      showToast(t('REDEEM_ERROR'), ToastType.Error);
    }
  };

  const handleRewardDetailsOpen = (withdrawalId: number) => {
    setSelectedTab(MyRewardsTabType.RewardHistory);
    setSelectedRewardDetailsWithdrawalId(withdrawalId);
    navigate('/my-rewards');
  };

  return (
    <Suspense fallback={null}>
      {accountDetails && isAdjustRewardAmountOpen && (
        <AdjustRewardAmount
          isOpen={isAdjustRewardAmountOpen}
          onAdjust={() => {
            setIsRewardAmountAdjusting(true);
            setRewardSelectionSelectedRewardId(reward?.withdrawalOptionId);
            setRewardSelectionMaxAvailablePoints(accountDetails.currentPointsBalance);
            setRewardSelectionOpen(true);
            setIsAdjustRewardAmountOpen(false);
          }}
          onClose={() => {
            setIsAdjustRewardAmountOpen(false);
          }}
        />
      )}
      {redeemReward && accountDetails && isReadyToRedeemOpen && (
        <ReadyToRedeem
          name={`${formatCurrency(redeemReward.amount, redeemReward.currencyCode)} ${redeemReward.name}`}
          image={redeemReward.imageSrc ?? ''}
          typeName={resolveRewardTypeName(redeemReward.type as WithdrawalOptionType)}
          type={redeemReward.type as WithdrawalOptionType}
          provider={currentProvider}
          pointsToRedeem={rewardPointsCost}
          remainingBalance={remainingBalance}
          canRedeem={canRedeem}
          hasSuccessfulWithdrawal={accountDetails.hasSuccessfulWithdrawal}
          isApprovedForWithdrawal={accountDetails.isApprovedForWithdrawal}
          isOpen={isReadyToRedeemOpen}
          onRedeem={async () => {
            await handleRedeem();
          }}
          onChangeReward={() => {
            setRedeemRewardSnapshot(null);
            setIsReadyToRedeemOpen(false);
            setRewardSelectionOpen(true);
          }}
          onClose={() => {
            setRedeemRewardSnapshot(null);
            setIsReadyToRedeemOpen(false);
          }}
        />
      )}
      {isPayPalWithdrawalEnabled &&
        redeemReward &&
        accountDetails &&
        currentProvider === WithdrawalProvider.PayPal &&
        isPayPalEmailInputOpen && (
          <PayPalEmailInput
            isOpen={isPayPalEmailInputOpen}
            onClose={() => setIsPayPalEmailInputOpen(false)}
            onConfirm={(email) => void handlePayPalEmailConfirm(email)}
            initialEmail={userSettings?.paypalEmail || undefined}
            currencyCode={redeemReward.currencyCode}
            amount={redeemReward.amount}
          />
        )}
      {isSuccessfulWithdrawalOpen && (
        <SuccessfulWithdrawal
          isOpen={isSuccessfulWithdrawalOpen}
          rewardName={reward ? `${formatCurrency(reward.amount, reward.currencyCode)} ${reward.name}` : ''}
          isProcessing={isLastWithdrawalProcessing}
          hasSuccessfulWithdrawal={accountDetails?.hasSuccessfulWithdrawal ?? false}
          onRewardDetailsOpen={() => {
            if (isTrustpilotInviteEligible) {
              setIsTrustpilotInviteEligible(false);
              return;
            }
            withdrawalId && handleRewardDetailsOpen(withdrawalId);
          }}
          onClose={() => {
            setIsSuccessfulWithdrawalOpen(false);
            void refreshUserSettings();
          }}
        />
      )}
      {rewardSelectionOpen && (
        <RewardSelection
          options={[]}
          isOpen={rewardSelectionOpen}
          selectedOptionId={rewardSelectionSelectedRewardId}
          maxAvailableRewardPoints={rewardSelectionMaxAvailablePoints}
          onClose={() => {
            setRewardSelectionOpen(false);
            setRewardSelectionSelectedRewardId(undefined);
            setRewardSelectionMaxAvailablePoints(undefined);
            setIsRewardAmountAdjusting(false);
          }}
          onConfirm={({ canProceedToRedeem, savedReward }) => {
            setRewardSelectionOpen(false);
            setRewardSelectionSelectedRewardId(undefined);
            setRewardSelectionMaxAvailablePoints(undefined);
            if (isRewardAmountAdjusting && canProceedToRedeem && savedReward) {
              setRedeemRewardSnapshot(savedReward);
              setIsReadyToRedeemOpen(true);
            }
            setIsRewardAmountAdjusting(false);
          }}
        />
      )}
    </Suspense>
  );
};
